import { useState } from "react";
import { Address, parseEventLogs, zeroAddress, zeroHash } from "viem";
import { polygon } from "viem/chains";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { Bot, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { INCEPTION_FACTORY_ABI, INCEPTION_FACTORY_ADDRESS } from "@/features/inception/config";
import { BindingHealthPanel } from "./BindingHealthPanel";
import {
  ERC8004_IDENTITY_ABI, ERC8004_IDENTITY_REGISTRY_ADDRESS, ERC8004_WALLET_DOMAIN_NAME,
  ERC8004_WALLET_DOMAIN_VERSION, INCEPTION_AGENT_BINDING_ABI,
  INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS, UNIVERSAL_IDENTITY_ABI,
  UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
} from "@/features/identity/config";

type Stage = "idle" | "entity" | "vault" | "agent" | "wallet" | "link" | "bind" | "complete";

export function HybridIdentityPanel() {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  const [entityType, setEntityType] = useState(1);
  const [entityURI, setEntityURI] = useState("");
  const [agentURI, setAgentURI] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentEndpoint, setAgentEndpoint] = useState("");
  const [capabilities, setCapabilities] = useState("feedback,validation,payments");
  const [stage, setStage] = useState<Stage>("idle");
  const [entityId, setEntityId] = useState<bigint>();
  const [agentId, setAgentId] = useState<bigint>();
  const [vault, setVault] = useState<Address>();
  const [walletVerified, setWalletVerified] = useState(false);
  const [agentLinked, setAgentLinked] = useState(false);

  const configured = Boolean(INCEPTION_FACTORY_ADDRESS && UNIVERSAL_IDENTITY_REGISTRY_ADDRESS
    && ERC8004_IDENTITY_REGISTRY_ADDRESS && INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS);

  const send = async (contract: Address, abi: readonly unknown[], functionName: string, args: readonly unknown[]) => {
    if (!address || !walletClient || !publicClient) throw new Error("Polygon wallet is unavailable.");
    if ((await walletClient.getChainId()) !== polygon.id) await walletClient.switchChain({ id: polygon.id });
    const simulation = await publicClient.simulateContract({ address: contract, abi, functionName, args, account: address } as never);
    const hash = await walletClient.writeContract(simulation.request);
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") throw new Error(`${functionName} reverted on Polygon.`);
    return receipt;
  };

  const createIdentity = async () => {
    if (!isConnected || !address || !walletClient || !publicClient) return toast({ title: "Connect a wallet", description: "A controller wallet is required.", variant: "destructive" });
    if (!configured || !INCEPTION_FACTORY_ADDRESS || !UNIVERSAL_IDENTITY_REGISTRY_ADDRESS
      || !ERC8004_IDENTITY_REGISTRY_ADDRESS || !INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS)
      return toast({ title: "Contracts unavailable", description: "Configure every identity and Inception address.", variant: "destructive" });
    if (!entityURI || (!agentURI && !agentName))
      return toast({ title: "Metadata required", description: "Provide entity metadata and either an agent name or an existing manifest URI.", variant: "destructive" });

    try {
      let nextEntityId = entityId;
      if (nextEntityId === undefined) {
        setStage("entity");
        const receipt = await send(UNIVERSAL_IDENTITY_REGISTRY_ADDRESS, UNIVERSAL_IDENTITY_ABI, "registerEntity", [entityType, entityURI, zeroHash]);
        nextEntityId = parseEventLogs({ abi: UNIVERSAL_IDENTITY_ABI, logs: receipt.logs, eventName: "EntityRegistered" })[0].args.entityId;
        setEntityId(nextEntityId);
      }
      let nextVault = vault;
      if (!nextVault) {
        setStage("vault");
        const receipt = await send(INCEPTION_FACTORY_ADDRESS, INCEPTION_FACTORY_ABI, "create", [zeroAddress, "", "", zeroAddress, zeroAddress]);
        nextVault = parseEventLogs({ abi: INCEPTION_FACTORY_ABI, logs: receipt.logs, eventName: "NewCollection" })[0].args.collectionAddress;
        setVault(nextVault);
      }

      let registrationURI = agentURI.trim();
      const expectedWallet = `eip155:${polygon.id}:${nextVault.toLowerCase()}`;
      if (!registrationURI) {
        const manifest = { name: agentName.trim(), version: "1.0.0", wallets: [expectedWallet],
          endpoints: agentEndpoint.trim() ? [{ type: "service", url: agentEndpoint.trim() }] : [],
          capabilities: capabilities.split(",").map((item) => item.trim()).filter(Boolean) };
        registrationURI = `data:application/json;base64,${btoa(JSON.stringify(manifest))}`;
      } else {
        const fetchURI = registrationURI.startsWith("ipfs://") ? `https://ipfs.io/ipfs/${registrationURI.slice(7)}` : registrationURI;
        const response = await fetch(fetchURI);
        if (!response.ok) throw new Error("The existing agent manifest could not be fetched for verification.");
        const manifest = await response.json();
        const wallets = Array.isArray(manifest.wallets) ? manifest.wallets.map((item: unknown) => String(item).toLowerCase()) : [];
        if (!wallets.includes(expectedWallet)) throw new Error(`Manifest must contain the vault wallet ${expectedWallet}.`);
      }

      let nextAgentId = agentId;
      if (nextAgentId === undefined) {
        setStage("agent");
        const receipt = await send(ERC8004_IDENTITY_REGISTRY_ADDRESS, ERC8004_IDENTITY_ABI, "register", [registrationURI]);
        nextAgentId = parseEventLogs({ abi: ERC8004_IDENTITY_ABI, logs: receipt.logs, eventName: "Registered" })[0].args.agentId;
        setAgentId(nextAgentId);
      }
      if (!walletVerified) {
        setStage("wallet");
        const deadline = BigInt(Math.floor(Date.now() / 1000) + 900);
        const nonce = await publicClient.readContract({ address: ERC8004_IDENTITY_REGISTRY_ADDRESS, abi: ERC8004_IDENTITY_ABI, functionName: "agentWalletNonces", args: [nextAgentId] } as never) as bigint;
        const signature = await walletClient.signTypedData({ account: address,
          domain: { name: ERC8004_WALLET_DOMAIN_NAME, version: ERC8004_WALLET_DOMAIN_VERSION, chainId: polygon.id, verifyingContract: ERC8004_IDENTITY_REGISTRY_ADDRESS },
          types: { SetAgentWallet: [{ name: "agentId", type: "uint256" }, { name: "newWallet", type: "address" }, { name: "nonce", type: "uint256" }, { name: "deadline", type: "uint256" }] },
          primaryType: "SetAgentWallet", message: { agentId: nextAgentId, newWallet: nextVault, nonce, deadline } });
        await send(ERC8004_IDENTITY_REGISTRY_ADDRESS, ERC8004_IDENTITY_ABI, "setAgentWallet", [nextAgentId, nextVault, deadline, signature]);
        setWalletVerified(true);
      }
      if (!agentLinked) {
        setStage("link");
        await send(UNIVERSAL_IDENTITY_REGISTRY_ADDRESS, UNIVERSAL_IDENTITY_ABI, "linkAgent", [nextEntityId, ERC8004_IDENTITY_REGISTRY_ADDRESS, nextAgentId]);
        setAgentLinked(true);
      }
      setStage("bind");
      await send(INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS, INCEPTION_AGENT_BINDING_ABI, "bindValidated", [nextEntityId, nextAgentId, INCEPTION_FACTORY_ADDRESS, BigInt(nextVault), true]);
      setStage("complete");
      toast({ title: "Hybrid identity created", description: `Agent ${nextAgentId} now uses its Inception vault as its verified wallet.` });
    } catch (error) {
      console.error(error);
      toast({ title: "Setup paused", description: `${error instanceof Error ? error.message : "Transaction failed."} Retry to continue.`, variant: "destructive" });
    }
  };

  const working = stage !== "idle" && stage !== "complete";
  return <Card><CardHeader><CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5" />ERC-8004 Inception identity</CardTitle><CardDescription>The controller owns the identities; the ERC-1271 vault is the verified operational wallet.</CardDescription></CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2"><Label htmlFor="identity-type">Controller type</Label><select id="identity-type" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={entityType} onChange={(event) => setEntityType(Number(event.target.value))}><option value={1}>Human</option><option value={2}>Organization</option><option value={3}>DAO</option><option value={4}>Device</option></select></div>
      <div className="space-y-2"><Label htmlFor="entity-uri">Entity metadata URI</Label><Input id="entity-uri" value={entityURI} onChange={(event) => setEntityURI(event.target.value)} placeholder="ipfs://…" /></div>
      <div className="space-y-2 rounded border p-3"><p className="text-sm font-medium">ERC-8004 manifest builder</p><div className="grid gap-2 sm:grid-cols-2"><Input value={agentName} onChange={(event) => setAgentName(event.target.value)} placeholder="Agent name" /><Input value={agentEndpoint} onChange={(event) => setAgentEndpoint(event.target.value)} placeholder="Service endpoint (optional)" /></div><Input value={capabilities} onChange={(event) => setCapabilities(event.target.value)} placeholder="Comma-separated capabilities" /><p className="text-xs text-muted-foreground">The generated manifest inserts the vault as a CAIP-10 wallet. Existing manifest URIs are fetched and checked for that wallet.</p><Input id="agent-uri" value={agentURI} onChange={(event) => setAgentURI(event.target.value)} placeholder="Existing ipfs:// or https:// manifest (optional)" /></div>
      <Button onClick={createIdentity} disabled={working || !configured}>{working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : stage === "complete" ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <Bot className="mr-2 h-4 w-4" />}{stage === "complete" ? "Identity active" : working ? `Confirm ${stage} step…` : "Create hybrid identity"}</Button>
      {!configured && <p className="text-xs text-destructive">The Inception and identity contracts are not fully configured.</p>}
      {(entityId !== undefined || agentId !== undefined || vault) && <div className="rounded-md border p-3 font-mono text-xs">{entityId !== undefined && <p>Entity: {entityId.toString()}</p>}{agentId !== undefined && <p>Agent: {agentId.toString()}</p>}{vault && <p className="break-all">Wallet: {vault}</p>}</div>}
      {agentId !== undefined && <BindingHealthPanel agentId={agentId} />}
    </CardContent></Card>;
}
